import { createContext } from 'react';
import type { CurrentUser } from '@/shared/api/types';
import type { Role } from './roles';

/**
 * How the user chose to authenticate:
 * - `sso` → CloudEQ SSO (Entra federation) — the only method, and the default.
 *
 * A `credentials` value used to exist here (a local Cognito account handed
 * off to the Cognito hosted login). It's gone: the 2026-08 Qfly-hosted
 * native-login cutover moved the credential form onto this page itself
 * (`LoginPage.tsx`'s embedded email/password state machine, calling
 * `POST /auth/login` directly) — it never calls `login()` at all. Narrowing
 * this union to `'sso'` makes `login('credentials')` a compile-time error,
 * so the old Cognito-hosted-UI credential redirect can't be reintroduced by
 * accident.
 */
export type LoginMethod = 'sso';

export interface AuthContextValue {
  readonly user: CurrentUser | null;
  readonly status: 'loading' | 'authenticated' | 'unauthenticated';
  readonly roles: readonly Role[];
  readonly regions: readonly string[];
  /** The access-management surfaces (manual-role grant/revoke, Native
   *  Accounts) gate on this. They sat behind a separate `isSuperAdmin` tier
   *  until it was retired (2026-09); there are three canonical roles and no
   *  fourth tier — do not reintroduce one here. */
  readonly isAdmin: boolean;
  readonly hasRole: (role: Role) => boolean;
  /** Redirect to the Cognito hosted login. Defaults to `sso`. */
  readonly login: (method?: LoginMethod) => void;
  readonly logout: () => Promise<void>;
}

/**
 * Auth context is exported from a dedicated module so hot-reload works
 * for AuthProvider (react-refresh requires component-only modules).
 */
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);
